import { Button, Heading } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { capitalizeFirst } from '@sendible/common';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { Container, Group, LoadingGroup } from './index.styles';
import { formatMonthYear, formatWeekRange, formatYear } from '../../utils';
import { useCalendarContext } from '../../context';
import { OptionsDropdown } from '../OptionsDropdown';
import { ModesDropdown } from '../ModesDropdown';

export const ConfigBar = () => {
  const { currentDate, isFetching, nextMonth, nextWeek, prevMonth, prevWeek, setCurrentDateToday, userWeekStartDay, viewMode } = useCalendarContext();
  const user = useBridgeUser();
  const { t } = useTranslation('calendar');

  const yearLable = useMemo(() => capitalizeFirst(formatYear(currentDate)), [currentDate]);
  const monthLabel = useMemo(() => capitalizeFirst(formatMonthYear(currentDate, user?.language)), [currentDate, user?.language]);
  const weekLabel = useMemo(
    () => capitalizeFirst(formatWeekRange(currentDate, userWeekStartDay, user?.language)),
    [currentDate, userWeekStartDay, user?.language]
  );

  return (
    <Container
      data-testid="calendar-toolbar"
      $viewMode={viewMode}
    >
      <Group>
        <Heading
          customClass={viewMode === 'daily' ? 'daily-heading' : ''}
          id="calendar-heading"
          semantic="h2"
          variation="component-title_20"
        >
          {viewMode === 'weekly' ? weekLabel : viewMode === 'daily' ? yearLable : monthLabel}
        </Heading>
        {isFetching && viewMode === 'daily' && (
          <LoadingGroup>
            <Heading
              variation="list-title_12"
              semantic="h4"
            >
              {t('loading')}
            </Heading>
          </LoadingGroup>
        )}
      </Group>
      <Group>
        {viewMode !== 'daily' && (
          <>
            <Button
              data-testid="calendar-toolbar-nav-back-button"
              icon="chevron_left"
              onClick={viewMode === 'weekly' ? prevWeek : prevMonth}
              size={12}
              data-export-hide
            />
            <Button
              data-testid="calendar-toolbar-nav-next-button"
              icon="chevron_right"
              onClick={viewMode === 'weekly' ? nextWeek : nextMonth}
              size={12}
              data-export-hide
            />
          </>
        )}
        <Button
          data-testid="calendar-toolbar-today-button"
          label={t('today') as string}
          onClick={setCurrentDateToday}
          size={12}
          variation="outline"
          data-export-hide
        />
        <ModesDropdown />
        <OptionsDropdown />
      </Group>
    </Container>
  );
};
